import type {
  StockOverviewListRequestType,
  StockOverviewListResponseType,
} from "@sellar-io/api-v2/types";
import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import { apiV2AssertResponse, apiV2Request } from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

export const useStockOverview = (
  params: StockOverviewListRequestType,
): UseInfiniteQueryResult<InfiniteData<StockOverviewListResponseType>> => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useInfiniteQuery({
    queryKey: [user?.uid, business?.id, ServerStateKeys.StockOverview, params],
    queryFn: ({ pageParam }) => {
      return apiV2Request({
        user,
        business,
        method: "get",
        path: "stock-overview",
        params: {
          ...params,
          offset: pageParam || undefined,
        },
      }).then(
        (data) => apiV2AssertResponse<StockOverviewListResponseType>(data).data,
      );
    },

    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flatMap((page) => page.data).length;
      if (totalFetched < lastPage.meta.count) {
        return totalFetched;
      }
      return undefined;
    },
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};
